import type { ErrorCode, GameBrandChangeReason, SetGameBrandsResult } from '@dayon/contracts';

/**
 * Which brands carry a title, changed for a reason that is recorded.
 *
 * `PUT /api/games/:id/brands` takes THE WHOLE CHIP SET rather than a diff. The
 * drawer draws one chip per brand and the operator taps some of them, so the
 * screen's state IS the whole set; asking the client to compute the diff would
 * put "what changed" where a stale render becomes a write. The route works out
 * which pairs moved and logs only those.
 */

/**
 * Three of the six reasons survive at brand level.
 *
 * SCHEDULED_MAINTENANCE, PROVIDER_INCIDENT and RTP_DEVIATION describe
 * something wrong with the TITLE or its PROVIDER, and none of those is true of
 * one brand and false of another — a provider outage does not stop at a brand
 * boundary. Offering them here would invite an operator to record a
 * platform-wide fact as a per-brand decision.
 */
export const BRAND_REASONS = [
  { code: 'CATALOGUE_LAUNCH', label: 'Catalogue launch or relaunch' },
  { code: 'COMMERCIAL_DECISION', label: 'Commercial decision' },
  { code: 'COMPLIANCE_HOLD', label: 'Compliance or certification hold' },
] as const satisfies readonly { code: GameBrandChangeReason; label: string }[];

export interface BrandPair {
  brandId: string;
  brand: string;
  enabled: boolean;
}

/**
 * What the operator has actually changed, for the confirmation.
 *
 * The WRITE still sends the whole set — this is only what the dialog names, so
 * a person can check the change before committing to it. "3 brands" is not
 * something anybody can verify; "on for FairPlay, off for Bingo Barrio" is.
 */
export function movedPairs(
  before: readonly BrandPair[],
  after: ReadonlyMap<string, boolean>,
): { brand: string; from: boolean; to: boolean }[] {
  const moved: { brand: string; from: boolean; to: boolean }[] = [];
  for (const pair of before) {
    const to = after.get(pair.brandId);
    if (to !== undefined && to !== pair.enabled)
      moved.push({ brand: pair.brand, from: pair.enabled, to });
  }
  return moved;
}

/**
 * WHAT THE SHELF LOOKS LIKE NOW — AND ONLY WITHIN THIS OPERATOR'S SCOPE.
 *
 * The response counts the caller's brands, not the platform's, and the field
 * names say so. For an operator restricted to some brands, zero means "on no
 * shelf YOU CAN SEE" and never "on no shelf anywhere" — the two readings
 * diverge exactly for the operator nobody checks by hand. A platform-wide
 * figure is deliberately absent from the response, because telling a
 * restricted operator how many other brands carry a title is a disclosure even
 * when it names none of them.
 *
 * So this sentence says "of your brands", every time, including when the
 * caller can see all of them. Wording that is only true for unrestricted
 * operators is wording that waits for a restricted one.
 */
export function shelfSentence(result: SetGameBrandsResult): string {
  const { name, enabledBrandCountInScope: on, brandCountInScope: total } = result;
  if (total === 0) return `${name} was updated. No brands are in your scope to count.`;
  if (on === 0)
    return `${name} is now on none of your ${total} brand${total === 1 ? '' : 's'}. It may still be on brands outside your scope — this count only covers yours.`;
  return `${name} is now on ${on} of your ${total} brand${total === 1 ? '' : 's'}.`;
}

/** One line per pair that moved, for the confirmation and the toast. */
export const pairSentence = (p: { brand: string; to: boolean }): string =>
  `${p.brand} · ${p.to ? 'on' : 'off'}`;

/**
 * Why a refused change was refused, as a heading.
 *
 * Same shape as the provider dialog's, and the same reasoning: the server's
 * sentence is always the message because it is more precise, and the TITLE
 * comes from what this session knows rather than from matching the prose —
 * `games.manage` and the two-factor rule both arrive as FORBIDDEN.
 *
 * There is no TWO_FACTOR_REQUIRED branch, for the reason recorded in
 * provider-status.ts: no back-office route can emit it.
 */
export function brandRefusalTitle(
  code: ErrorCode | undefined,
  gameName: string,
  session: { allowed: boolean; mfa: boolean },
): string {
  if (code === 'FORBIDDEN' || code === 'UNAUTHENTICATED') {
    if (!session.allowed) return 'That needs the games.manage permission';
    if (!session.mfa) return 'That needs a two-factor verified session';
    return `${gameName}'s shelf was not changed`;
  }
  if (code === 'CONFLICT') return `${gameName}'s shelf was not changed`;
  if (code === 'VALIDATION_FAILED') return 'That change was not accepted';
  if (code === 'NOT_FOUND') return `${gameName} no longer exists`;
  return `${gameName}'s shelf was not changed`;
}
